import { create } from "zustand";
import { persist } from "zustand/middleware";
import { EPaymentMethod } from "../models/EPaymentMethod";
import type IBasketAccount from "./models/IBasketItem.models";
import type { IPrice } from "./models/IPrice";

interface BasketState {
  email: string | undefined;
  setEmail: (email: string) => void;

  paymentMethod: EPaymentMethod;
  setPaymentMethod: (method: EPaymentMethod) => void;

  // Items Basket
  accounts: IBasketAccount[];
  // Items Basket Functions
  addBasketAccount: (item: IBasketAccount) => void;
  addBasketAccounts: (items: IBasketAccount[]) => void;
  deleteBasketAccount: (accountId: number) => void;
  setBasketAccounts: (items: IBasketAccount[]) => void;

  // Price
  price: IPrice;
  // Price Functions
  setPrice: (price: IPrice) => void;

  // Voucher
  voucherInfo: { voucher?: string; isValid: boolean; isFilled: boolean };
  // Voucher Functions
  setVoucherInfo: (voucherInfo: {
    voucher?: string;
    isValid?: boolean;
    isFilled?: boolean;
  }) => void;
  removeVoucherInfo: () => void;

  // Affiliation Code
  affiliationCodeInfo: { code?: string; isValid: boolean; isFilled: boolean };
  // Affiliation Code Functions
  setAffiliationCodeInfo: (affiliationCodeInfo: {
    code?: string;
    isValid?: boolean;
    isFilled?: boolean;
  }) => void;
  removeAffiliationCodeInfo: () => void;

  // General Functions
  clear: () => void;
}

export const useBasketStore = create<BasketState>()(
  persist(
    (set, get) => ({
      email: undefined,
      setEmail: (email) => {
        if (get().email === email) return;
        set({ email });
      },

      paymentMethod: EPaymentMethod.NONE,
      setPaymentMethod: (method) => {
        if (get().paymentMethod === method) return;

        set({ paymentMethod: method });
      },

      accounts: [],
      addBasketAccount: (item) => {
        if (get().accounts.some((i) => i.accountId === item.accountId)) {
          return;
        }

        set((state) => ({ accounts: [...state.accounts, item] }));
      },
      addBasketAccounts: (items) => {
        const uniqueItems = items.filter(
          (item) => !get().accounts.some((i) => i.accountId === item.accountId),
        );

        set((state) => ({ accounts: [...state.accounts, ...uniqueItems] }));
      },
      deleteBasketAccount: (accountId) => {
        const newItems = get().accounts.filter(
          (item) => item.accountId !== accountId,
        );

        set(() => ({
          accounts: [...newItems],
        }));
      },
      setBasketAccounts: (accounts) => {
        set({ accounts });
      },

      price: {
        total: undefined,
        discount: undefined,
        fees: undefined,
        subTotal: undefined,
      },
      setPrice: (price) => {
        set({ price });
      },

      voucherInfo: {
        voucher: undefined,
        isFilled: false,
        isValid: false,
      },

      setVoucherInfo: ({ isFilled, isValid, voucher }) => {
        const voucherInfo = get().voucherInfo;

        if (isFilled !== undefined) voucherInfo.isFilled = isFilled;

        if (isValid !== undefined) voucherInfo.isValid = isValid;

        if (voucher !== undefined) voucherInfo.voucher = voucher;

        set({ voucherInfo: voucherInfo });
      },
      removeVoucherInfo: () => set({ voucherInfo: undefined }),

      affiliationCodeInfo: { isFilled: false, isValid: false, code: undefined },

      setAffiliationCodeInfo: ({ isFilled, isValid, code }) => {
        const affiliationCodeInfo = get().affiliationCodeInfo;

        if (isFilled !== undefined && isFilled !== affiliationCodeInfo.isFilled)
          affiliationCodeInfo.isFilled = isFilled;

        if (isValid !== undefined && isValid !== affiliationCodeInfo.isValid)
          affiliationCodeInfo.isValid = isValid;

        if (code !== undefined) affiliationCodeInfo.code = code;

        set({ affiliationCodeInfo: affiliationCodeInfo });
      },
      removeAffiliationCodeInfo: () => set({ voucherInfo: undefined }),
      clear: () => {
        // replace items only if items are filled
        // to avoid basket state infinite loop
        set({
          accounts: get().accounts.length > 0 ? [] : get().accounts,
          voucherInfo: { isFilled: false, isValid: false, voucher: undefined },
          affiliationCodeInfo: {
            isFilled: false,
            isValid: false,
            code: undefined,
          },
          price: {
            discount: undefined,
            subTotal: undefined,
            total: undefined,
            fees: undefined,
          },
        });
      },
    }),
    { name: "SecretSmurfsStore-basket" },
  ),
);

export const useBasketItems = () => {
  return useBasketStore((state) => state.accounts);
};
